/**
 * Builds the self-contained HTML for a referral-partner **payout receipt**.
 *
 * This is the modern, single-page receipt a partner keeps as proof of payment.
 * It is written into a print window by `openPrintWindow`, so every style has to
 * travel with the document — the dashboard's Tailwind theme is not available
 * there. The layout follows the same teal brand, letterhead and signature block
 * as the invoice / payment receipts so every financial document a partner
 * receives looks like it came from the same hospital.
 *
 * "Logical" structure, in the order a reader needs it:
 *   1. Who was paid and which receipt this is,
 *   2. how much and in words,
 *   3. what it settled (the referral share line items) and the totals,
 *   4. who prepared it and where to ask questions (footer).
 */

import { amountInWords } from './paymentReceipt';

const CURRENCY = '৳';

const METHOD_LABELS: Record<string, string> = {
  cash: 'Cash',
  bank: 'Bank Transfer',
  bank_transfer: 'Bank Transfer',
  bkash: 'bKash',
  nagad: 'Nagad',
  card: 'Card',
  upi: 'UPI',
  insurance: 'Insurance',
};

export interface ReferralPayoutReceiptHospital {
  name: string;
  address?: string;
  email?: string;
  website?: string;
  hotline?: string;
  logo?: string;
  certified?: string;
  signature?: string;
  seal?: string;
  signatoryName?: string;
}

export interface ReferralPayoutReceiptLine {
  /** 'Consultation Fee' | 'Diagnostic Referral' | free text. */
  source?: string;
  description?: string;
  /** Date of the originating receipt / appointment (already formatted). */
  date?: string;
  /** Receipt ID for diagnostics (consultations have none). */
  reference?: string;
  /** Payee share percentage for this line (e.g. 10). */
  sharePercent?: number;
  billedAmount?: number;
  payeeShare?: number;
}

export interface ReferralPayoutReceiptData {
  paymentNumber?: string;
  status?: string;
  payeeName?: string;
  payeeCode?: string;
  organization?: string;
  designation?: string;
  mobileNumber?: string;
  bankName?: string;
  bankAccountNumber?: string;
  method?: string;
  paidAt?: string;
  distributionCount?: number;
  preparedBy?: string;
  notes?: string;
  isOpening?: boolean;
  externalReference?: string;
  /** Amount actually paid (authoritative figure on the voucher). */
  amount?: number;
  billedTotal?: number;
  shareTotal?: number;
  lines: ReferralPayoutReceiptLine[];
}

function escapeHtml(value: unknown): string {
  return String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function fmtMoney(value: number | undefined | null): string {
  const n = Number(value) || 0;
  const sign = n < 0 ? '−' : '';
  return `${sign}${CURRENCY}${Math.abs(n).toLocaleString('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  })}`;
}

function fmtNum(value: number | undefined | null): string {
  return (Number(value) || 0).toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function fmtPct(value: number | undefined | null): string {
  return `${(Number(value) || 0).toLocaleString('en-US', { maximumFractionDigits: 2 })}%`;
}

function fmtDate(value?: string | null): { date: string; time: string } {
  if (!value) return { date: '—', time: '' };
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return { date: '—', time: '' };
  return {
    date: d.toLocaleDateString('en-GB', { year: 'numeric', month: '2-digit', day: '2-digit' }),
    time: d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }),
  };
}

export function buildReferralPayoutReceiptHtml({
  data,
  hospital,
  printedBy,
}: {
  data: ReferralPayoutReceiptData;
  hospital: ReferralPayoutReceiptHospital;
  printedBy?: string;
}): string {
  const amount = Number(data.amount || 0);
  const isPaid = (data.status || 'PAID').toUpperCase() === 'PAID';
  const { date: dateStr, time: timeStr } = fmtDate(data.paidAt);

  const methodLabel = METHOD_LABELS[(data.method || 'cash').toLowerCase()] || data.method || 'Cash';
  const payeeLine = [data.designation, data.organization].filter(Boolean).join(' · ');

  const contact = [
    hospital.hotline ? `Hotline: ${hospital.hotline}` : '',
    hospital.email,
    hospital.website,
  ]
    .filter(Boolean)
    .join(' · ');

  const shareTotal =
    data.shareTotal ?? data.lines.reduce((sum, l) => sum + (Number(l.payeeShare) || 0), 0);

  const rows = data.lines
    .map(
      (line) => `
        <tr>
          <td class="src">${escapeHtml(line.source || 'Referral')}</td>
          <td class="dt">${escapeHtml(line.date || '—')}</td>
          <td class="ref">${escapeHtml(line.reference || '—')}</td>
          <td class="num">${line.sharePercent == null ? '—' : escapeHtml(fmtPct(line.sharePercent))}</td>
          <td class="num strong">${escapeHtml(fmtNum(line.payeeShare))}</td>
        </tr>`,
    )
    .join('');

  const bankLine =
    data.bankName || data.bankAccountNumber
      ? `<div class="row"><span class="k">Bank</span><span class="v">${escapeHtml(
          [data.bankName, data.bankAccountNumber].filter(Boolean).join(' · '),
        )}</span></div>`
      : '';

  return `<!doctype html>
<html>
<head>
<meta charset="utf-8" />
<title>Payout Receipt ${escapeHtml(data.paymentNumber || '')}</title>
<style>
  * { box-sizing: border-box; }
  body { font-family: 'Segoe UI', Arial, Helvetica, sans-serif; color: #0f172a; margin: 0; padding: 32px; background: #ffffff; font-size: 12px; }
  .doc { max-width: 820px; margin: 0 auto; }

  .accent { height: 6px; border-radius: 999px; background: linear-gradient(90deg, #0f766e 0%, #0f766ecc 45%, #e2e8f0 100%); }

  .header { display: flex; justify-content: space-between; align-items: flex-start; gap: 24px; padding: 16px 0 18px; border-bottom: 3px solid #0f766e; }
  .brand { display: flex; align-items: flex-start; gap: 12px; min-width: 0; }
  .brand .logo { height: 52px; width: 52px; border-radius: 12px; object-fit: contain; }
  .brand .mark { height: 52px; width: 52px; border-radius: 12px; background: #0f766e1a; color: #0f766e; display: flex; align-items: center; justify-content: center; font-size: 22px; font-weight: 800; }
  .brand h1 { margin: 0; font-size: 20px; font-weight: 800; color: #0f172a; }
  .brand .sub { font-size: 11px; color: #64748b; margin-top: 3px; line-height: 1.5; }
  .title { text-align: right; white-space: nowrap; }
  .title .label { font-size: 18px; font-weight: 800; letter-spacing: 1.4px; text-transform: uppercase; color: #0f766e; }
  .title .no { font-size: 11px; color: #64748b; margin-top: 4px; }
  .badge { display: inline-block; margin-top: 6px; padding: 3px 11px; border-radius: 999px; font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: .5px; background: ${isPaid ? '#dcfce7' : '#fee2e2'}; color: ${isPaid ? '#15803d' : '#b91c1c'}; }

  .grid { display: flex; border: 1px solid #e2e8f0; border-radius: 10px; overflow: hidden; margin-top: 20px; }
  .grid .cell { flex: 1; padding: 12px 16px; border-right: 1px solid #e2e8f0; }
  .grid .cell.wide { flex: 1.6; }
  .grid .cell:last-child { border-right: 0; }
  .grid h3 { margin: 0 0 6px; font-size: 9px; font-weight: 800; letter-spacing: 1.2px; text-transform: uppercase; color: #64748b; }
  .grid p { margin: 0; line-height: 1.6; }
  .strong { font-weight: 700; }

  .amount-band { display: flex; justify-content: space-between; align-items: center; gap: 24px; margin-top: 18px; padding: 16px 20px; border-radius: 12px; background: #0f766e0f; border: 1px solid #0f766e33; }
  .amount-band .label { font-size: 10px; font-weight: 800; letter-spacing: 1.4px; text-transform: uppercase; color: #0f766e; }
  .amount-band .words { font-size: 11px; color: #475569; margin-top: 6px; line-height: 1.5; max-width: 440px; }
  .amount-band .value { font-size: 30px; font-weight: 800; color: #0f766e; white-space: nowrap; letter-spacing: -.5px; }

  table.lines { width: 100%; border-collapse: collapse; margin-top: 20px; font-size: 11.5px; }
  table.lines th, table.lines td { border: 1px solid #e2e8f0; padding: 7px 9px; text-align: left; }
  table.lines th { background: #f8fafc; font-size: 9px; font-weight: 800; letter-spacing: .6px; text-transform: uppercase; color: #64748b; }
  table.lines td.num, table.lines th.num { text-align: right; white-space: nowrap; }
  table.lines td.src { white-space: nowrap; color: #334155; }
  table.lines td.dt { white-space: nowrap; color: #334155; }
  table.lines td.ref { white-space: nowrap; color: #334155; font-family: ui-monospace, SFMono-Regular, Menlo, monospace; }
  table.lines tbody tr:nth-child(even) { background: #fbfdfd; }

  .bottom { display: flex; justify-content: space-between; align-items: flex-start; gap: 32px; margin-top: 20px; }
  .details { flex: 1; min-width: 0; }
  .details .row { display: flex; justify-content: space-between; gap: 16px; padding: 7px 0; border-bottom: 1px dashed #e2e8f0; font-size: 12px; }
  .details .row:last-child { border-bottom: 0; }
  .details .row .k { color: #64748b; }
  .details .row .v { font-weight: 600; text-align: right; }
  .details h4 { margin: 0 0 6px; font-size: 9px; font-weight: 800; letter-spacing: 1px; text-transform: uppercase; color: #64748b; }
  .notes { margin-top: 12px; }
  .notes p { margin: 0; font-size: 11px; color: #475569; line-height: 1.6; white-space: pre-wrap; }

  .totals { width: 300px; border: 1px solid #e2e8f0; border-radius: 10px; overflow: hidden; }
  .totals .head { background: #0f172a; color: #ffffff; padding: 8px 16px; font-size: 9px; font-weight: 800; letter-spacing: 1.2px; text-transform: uppercase; }
  .totals .body { padding: 10px 16px; }
  .totals .foot { border-top: 1px solid #e2e8f0; background: #f8fafc; padding: 10px 16px; }
  .totals .line { display: flex; justify-content: space-between; padding: 4px 0; font-size: 12px; }
  .totals .foot .line { font-weight: 700; }
  .totals .foot .line.grand { font-size: 15px; color: #0f766e; }

  .signature-block { display: flex; justify-content: flex-end; margin-top: 52px; }
  .signature-box { position: relative; width: 250px; text-align: center; }
  .signature-box .signature-img { height: 54px; max-width: 200px; object-fit: contain; display: block; margin: 0 auto 2px; }
  .signature-box .seal-img { position: absolute; top: -16px; right: -12px; height: 72px; width: 72px; object-fit: contain; opacity: .85; }
  .signature-box .line { border-top: 1px solid #0f172a; margin-top: 6px; }
  .signature-box .signature-label { margin-top: 4px; font-size: 11px; color: #374151; }
  .signature-box .signatory-name { margin-top: 2px; font-size: 10px; color: #64748b; }

  .footer { margin-top: 40px; border-top: 1px solid #e2e8f0; padding-top: 12px; font-size: 10px; color: #64748b; text-align: center; line-height: 1.6; }
  .footer .note { color: #94a3b8; margin-top: 4px; }

  @media print { @page { margin: 0; } body { padding: 14mm 12mm; } }
</style>
</head>
<body>
  <div class="doc">
    <div class="accent"></div>

    <div class="header">
      <div class="brand">
        ${hospital.logo
          ? `<img class="logo" src="${escapeHtml(hospital.logo)}" alt="logo" />`
          : `<div class="mark">${escapeHtml((hospital.name || 'H').trim().charAt(0).toUpperCase())}</div>`}
        <div>
          <h1>${escapeHtml(hospital.name)}</h1>
          ${hospital.address ? `<div class="sub">${escapeHtml(hospital.address)}</div>` : ''}
          ${contact ? `<div class="sub">${escapeHtml(contact)}</div>` : ''}
        </div>
      </div>
      <div class="title">
        <div class="label">Payout Receipt</div>
        <div class="no">Voucher No. ${escapeHtml(data.paymentNumber || '—')}</div>
        <span class="badge">${escapeHtml((data.status || 'PAID').toUpperCase())}</span>
      </div>
    </div>

    <div class="grid">
      <div class="cell wide">
        <h3>Paid To</h3>
        <p class="strong">${escapeHtml(data.payeeName || 'Referral Partner')}</p>
        ${payeeLine ? `<p>${escapeHtml(payeeLine)}</p>` : ''}
        ${data.payeeCode ? `<p>Code: ${escapeHtml(data.payeeCode)}</p>` : ''}
        ${data.mobileNumber ? `<p>${escapeHtml(data.mobileNumber)}</p>` : ''}
      </div>
      <div class="cell">
        <h3>Payment Date</h3>
        <p class="strong">${escapeHtml(dateStr)}</p>
        ${timeStr ? `<p>${escapeHtml(timeStr)}</p>` : ''}
      </div>
      <div class="cell">
        <h3>Method</h3>
        <p class="strong">${escapeHtml(methodLabel)}</p>
      </div>
      <div class="cell">
        <h3>Records Settled</h3>
        <p class="strong">${escapeHtml(data.distributionCount ?? data.lines.length)}</p>
      </div>
    </div>

    <div class="amount-band">
      <div>
        <div class="label">Amount Paid</div>
        <div class="words">${escapeHtml(amountInWords(amount))}</div>
      </div>
      <div class="value">${escapeHtml(fmtMoney(amount))}</div>
    </div>

    <table class="lines">
      <thead>
        <tr>
          <th>Source</th>
          <th>Date</th>
          <th>Reference ID</th>
          <th class="num">Share %</th>
          <th class="num">Payee Share</th>
        </tr>
      </thead>
      <tbody>
        ${rows || '<tr><td colspan="5" style="text-align:center;color:#94a3b8;padding:14px;">No line items</td></tr>'}
      </tbody>
    </table>

    <div class="bottom">
      <div class="details">
        <div class="row"><span class="k">Voucher No.</span><span class="v">${escapeHtml(data.paymentNumber || '—')}</span></div>
        <div class="row"><span class="k">Payment Type</span><span class="v">${data.isOpening ? 'Opening balance (legacy)' : 'Revenue settlement'}</span></div>
        ${data.externalReference ? `<div class="row"><span class="k">Original Ref.</span><span class="v">${escapeHtml(data.externalReference)}</span></div>` : ''}
        ${bankLine}
        <div class="row"><span class="k">Prepared By</span><span class="v">${escapeHtml(data.preparedBy || '—')}</span></div>
        ${data.notes
          ? `<div class="notes"><h4>Remarks</h4><p>${escapeHtml(data.notes)}</p></div>`
          : ''}
      </div>
      <div class="totals">
        <div class="head">Settlement Summary</div>
        <div class="body">
          <div class="line"><span>Sum of Payee Shares</span><span>${escapeHtml(fmtMoney(shareTotal))}</span></div>
        </div>
        <div class="foot">
          <div class="line grand"><span>Amount Paid</span><span>${escapeHtml(fmtMoney(amount))}</span></div>
        </div>
      </div>
    </div>

    <div class="signature-block">
      <div class="signature-box">
        ${hospital.seal ? `<img class="seal-img" src="${escapeHtml(hospital.seal)}" alt="Hospital seal" />` : ''}
        ${hospital.signature ? `<img class="signature-img" src="${escapeHtml(hospital.signature)}" alt="Authorized signature" />` : ''}
        <div class="line"></div>
        <div class="signature-label">Authorized Signature</div>
        ${hospital.signatoryName ? `<div class="signatory-name">${escapeHtml(hospital.signatoryName)}</div>` : ''}
      </div>
    </div>

    <div class="footer">
      ${hospital.name ? `<div>${escapeHtml(hospital.name)}</div>` : ''}
      ${hospital.certified ? `<div>${escapeHtml(hospital.certified)}</div>` : ''}
      <div class="note">
        This is the official proof of payment for the referral share listed above. Please retain it for your records.
        ${printedBy ? `Printed by ${escapeHtml(printedBy)} on ${escapeHtml(new Date().toLocaleString())}.` : `Printed on ${escapeHtml(new Date().toLocaleString())}.`}
      </div>
    </div>
  </div>
</body>
</html>`;
}
